/**
 * Service Providers settings (BE-3a) — payees for government fees, fines and add-ons.
 *
 * Route: /settings/service-providers
 * Government portals (ICP, GDRFA, MoHRE, EHS, MOFA, TAWJEEH) are seeded by the backend; their name and type are fixed,
 * contact details can change. Used as "Paid to" in the work order payment dialog.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DataTable, StatusChip } from '../../components/accounting';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { can } from '../../utils/roleUtils';
import { serviceProviderService } from '../../services/accounting/serviceProvider.service';
import { serverMessage } from '../../utils/apiConvert';

export const PROVIDER_TYPES = [
  { value: 'GOVT_PORTAL', label: 'Govt Portal', tone: 'info' },
  { value: 'AMER_CENTER', label: 'AMER Center', tone: 'warning' },
  { value: 'TASHEEL_CENTER', label: 'TASHEEL Center', tone: 'warning' },
  { value: 'TRANSLATION', label: 'Translation Office', tone: 'secondary' },
  { value: 'MEDICAL', label: 'Medical Center', tone: 'success' },
  { value: 'OTHER', label: 'Other', tone: 'default' },
];
const typeOf = (v) => PROVIDER_TYPES.find((t) => t.value === v) || { label: v, tone: 'default' };
const EMPTY = { name: '', type: 'OTHER', contactInfo: '', active: true };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const columns = (onEdit) => [
  { key: 'name', label: 'Provider Name', width: '28%', render: (r) => <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>{r.name}</Typography> },
  { key: 'type', label: 'Type', width: '18%', render: (r) => <StatusChip tone={typeOf(r.type).tone} label={typeOf(r.type).label} /> },
  { key: 'contactInfo', label: 'Contact / URL', width: '22%', render: (r) => <Typography sx={{ fontSize: fs.sm }}>{r.contactInfo || '—'}</Typography> },
  { key: 'isSystemSeeded', label: 'Source', width: '10%', render: (r) => <StatusChip tone={r.isSystemSeeded ? 'default' : 'primary'} label={r.isSystemSeeded ? 'System' : 'Custom'} /> },
  { key: 'active', label: 'Status', width: '10%', render: (r) => <StatusChip tone={r.active !== false ? 'success' : 'default'} label={r.active !== false ? 'Active' : 'Inactive'} /> },
  ...(onEdit ? [{
    key: 'actions', label: '', width: '8%', align: 'right',
    render: (r) => (
      <Button size="small" onClick={() => onEdit(r)} sx={{ textTransform: 'none', minWidth: 0, px: 1 }}>Edit</Button>
    ),
  }] : []),
];

const ServiceProviders = () => {
  const canWrite = can(sessionUser(), 'settings', 'write');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await serviceProviderService.list());
    } catch (err) {
      setError(serverMessage(err, 'Failed to load service providers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (p) => {
    setForm({ name: p.name, type: p.type, contactInfo: p.contactInfo || '', active: p.active !== false });
    setEditing(p);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const data = { name: form.name.trim(), type: form.type, contactInfo: form.contactInfo.trim() || null, active: form.active };
    try {
      if (editing?.id) await serviceProviderService.update(editing.id, data);
      else await serviceProviderService.create(data);
      setSnackbar(editing?.id ? 'Provider updated' : 'Provider added');
      setEditing(null);
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to save the provider'));
    } finally {
      setBusy(false);
    }
  };

  const seeded = !!editing?.isSystemSeeded;

  return (
    <Box sx={{ width: 0, minWidth: '100%', p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: fw.semibold }}>Service Providers</Typography>
          <Typography variant="body2" color="text.secondary">
            Govt portals and service providers your TC pays fees to. Used in Work Order &quot;Paid To&quot; dropdown.
          </Typography>
        </Box>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>
            Add Provider
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 640 }}>
          <DataTable
            columns={columns(canWrite ? openEdit : null)}
            data={rows}
            loading={loading}
            emptyMessage="No service providers configured."
            pagination={false}
          />
        </Box>
      </Box>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: fw.semibold }}>
          {editing?.id ? 'Edit Provider' : 'Add Service Provider'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Provider Name" size="small" value={form.name} disabled={seeded}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Al Quoz AMER Center" />
            <TextField select label="Type" size="small" value={form.type} disabled={seeded}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              {PROVIDER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField label="Contact / URL" size="small" value={form.contactInfo}
              onChange={(e) => setForm((f) => ({ ...f, contactInfo: e.target.value }))} placeholder="Phone, website, or address" />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
              label={form.active ? 'Active' : 'Inactive'}
            />
            {seeded && <Typography variant="caption" color="text.secondary">System government portal: only contact details and active can change.</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={busy || !form.name.trim()} onClick={save} sx={{ textTransform: 'none' }}>
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default ServiceProviders;
